import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
    Box, Grid, Paper, Typography, TextField, MenuItem, Select, FormControl,
    InputLabel, Chip, CircularProgress, Alert, List,
    ListItem, ListItemAvatar, ListItemText, Avatar, Divider, Button
} from '@mui/material';
import {
    Refresh, Search, DirectionsCar, People, FilterList, HourglassEmpty
} from '@mui/icons-material';

import { LORRY_MAP_ICON_BASE64, TRACTOR_MAP_ICON_BASE64 } from '../assets/vehicleMapIcons';

// ── Icons Custom Styles for Leaflet Markers ───────────────────
// We use L.divIcon to generate premium, sharp-looking markers with pure HTML & CSS.
const createDriverMarkerHtml = (driver, isAvailable) => {
    const isTractor = (driver.vehicle?.type || driver.vehicleType || '').toLowerCase() === 'tractor';
    const iconUrl = isTractor ? TRACTOR_MAP_ICON_BASE64 : LORRY_MAP_ICON_BASE64;
    const statusColor = isAvailable ? '#4caf50' : '#ff9800';
    return `
    <div style="position: relative; width: 48px; height: 48px;">
        <img src="${iconUrl}" style="width: 48px; height: 48px; object-fit: contain; filter: drop-shadow(0px 3px 6px rgba(0,0,0,0.4));" />
        <div style="position: absolute; bottom: 0; right: 0; width: 12px; height: 12px; border-radius: 50%; background-color: ${statusColor}; border: 2px solid white;"></div>
    </div>`;
};

const createCustomerMarkerHtml = (booking) => {
    return `
    <div style="
        background: #f44336; 
        width: 34px; 
        height: 34px; 
        border-radius: 50%; 
        border: 3px solid white; 
        box-shadow: 0 4px 12px rgba(244,67,54,0.4);
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
    ">
        <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 -960 960 960" width="18" fill="white">
            <path d="M480-480q33 0 56.5-23.5T560-560q0-33-23.5-56.5T480-640q-33 0-56.5 23.5T400-560q0 33 23.5 56.5T480-480Zm0 294q122-112 181-203.5T720-563q0-109-75.5-183T480-820q-109 0-184.5 74T220-563q0 71 59 162.5T480-186Zm0 106Q319-215 239.5-322.5T160-563q0-150 96.5-243.5T480-900q147 0 243.5 93.5T820-563q0 148-79.5 255.5T480-80Zm0-480Z"/>
        </svg>
    </div>`;
};

const createZoneMarkerHtml = (zone) => {
    return `
    <div style="
        background: rgba(17, 102, 17, 0.85); 
        padding: 4px 8px; 
        border-radius: 4px; 
        border: 1px solid white; 
        box-shadow: 0 2px 6px rgba(0,0,0,0.25);
        color: white;
        font-weight: 700;
        font-size: 11px;
        white-space: nowrap;
        font-family: sans-serif;
    ">
        📍 ${zone.name}
    </div>`;
};

const STATUS_CONFIG = {
    searching_driver: { label: 'Searching Driver', color: '#ff9800' },
    confirmed: { label: 'Driver Assigned', color: '#2196f3' },
    driver_arrived: { label: 'Driver Arrived', color: '#9c27b0' },
    in_progress: { label: 'In Progress', color: '#00bcd4' },
    pending: { label: 'Pending', color: '#757575' },
};

/* ── Loader for Leaflet CDN ────────────────────────────────── */
const loadLeafletAssets = () => {
    return new Promise((resolve, reject) => {
        if (window.L) {
            resolve(window.L);
            return;
        }

        // Add CSS
        if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link');
            link.id = 'leaflet-css';
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
        }

        // Add JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => {
            if (window.L) resolve(window.L);
            else reject(new Error('Leaflet load failed'));
        };
        script.onerror = () => reject(new Error('Leaflet script error'));
        document.head.appendChild(script);
    });
};

const LiveMap = () => {
    const [bookings, setBookings] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filters State
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedZone, setSelectedZone] = useState('all');
    const [driverAvailFilter, setDriverAvailFilter] = useState('all');
    const [selectedVehicleType, setSelectedVehicleType] = useState('all');
    
    // Map References
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef({ drivers: {}, bookings: {}, zones: {}, lines: {} });
    const [leafletLoaded, setLeafletLoaded] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null); // bookingId or driverId

    const fetchLiveMapData = useCallback(async (isInitial = false) => {
        if (isInitial) setLoading(true);
        try {
            const res = await axios.get('/admin/live-map-data');
            setBookings(res.data.bookings || []);
            setDrivers(res.data.drivers || []);
            setZones(res.data.zones || []);
            setError('');
        } catch (err) {
            console.error('Live Map Fetch Error:', err);
            setError('Failed to fetch live tracking data');
        } finally {
            if (isInitial) setLoading(false);
        }
    }, []);

    // Initial Load & script inclusion
    useEffect(() => {
        loadLeafletAssets()
            .then(() => {
                setLeafletLoaded(true);
                return fetchLiveMapData(true);
            })
            .catch(err => {
                console.error(err);
                setError('Failed to load map scripting dependencies.');
                setLoading(false);
            });
    }, [fetchLiveMapData]);

    // Live Polling every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            fetchLiveMapData(false);
        }, 10000);
        return () => clearInterval(interval);
    }, [fetchLiveMapData]);

    // Initialize Map Instance
    useEffect(() => {
        if (leafletLoaded && mapRef.current && !mapInstance.current) {
            const L = window.L;
            // Default center India coords if no drivers/bookings
            mapInstance.current = L.map(mapRef.current, {
                zoomControl: true,
                attributionControl: false
            }).setView([20.5937, 78.9629], 5);

            L.tileLayer('https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap France | &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 20,
                maxNativeZoom: 19
            }).addTo(mapInstance.current);
        }
        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, [leafletLoaded]);

    // Update markers based on current filtered dataset
    useEffect(() => {
        if (!leafletLoaded || !mapInstance.current) return;
        const L = window.L;

        const currentMarkers = markersRef.current;

        // --- FILTERING LOGIC ---
        const filteredBookings = bookings.filter(b => {
            const matchesSearch = !searchQuery || 
                b.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                b.customer?.phoneNumber?.includes(searchQuery) ||
                b._id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                b.pickupAddress?.toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchesStatus = selectedStatus === 'all' || b.status === selectedStatus;
            const matchesVehicle = selectedVehicleType === 'all' || b.vehicleType === selectedVehicleType;
            return matchesSearch && matchesStatus && matchesVehicle;
        });

        const filteredDrivers = drivers.filter(d => {
            const matchesSearch = !searchQuery || 
                d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                d.phoneNumber?.includes(searchQuery) ||
                d.vehicle?.vehicleNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                d.lastAddress?.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesAvail = 
                driverAvailFilter === 'all' || 
                (driverAvailFilter === 'available' && d.isAvailable) || 
                (driverAvailFilter === 'busy' && !d.isAvailable);

            const matchesVehicle = selectedVehicleType === 'all' || d.vehicle?.type === selectedVehicleType;
            return matchesSearch && matchesAvail && matchesVehicle;
        });

        // ── 1. Render Zones Boundaries & Center Markers ────────────
        // Clear old ones
        Object.keys(currentMarkers.zones).forEach(id => {
            mapInstance.current.removeLayer(currentMarkers.zones[id].circle);
            mapInstance.current.removeLayer(currentMarkers.zones[id].marker);
            delete currentMarkers.zones[id];
        });

        zones.forEach(z => {
            if (selectedZone !== 'all' && z._id !== selectedZone) return;
            if (z.latitude && z.longitude) {
                // Circle styling
                const circle = L.circle([z.latitude, z.longitude], {
                    radius: (z.radius || 10) * 1000, // radius is km, convert to meters
                    color: '#116611',
                    fillColor: '#99CC77',
                    fillOpacity: 0.1,
                    weight: 1.5,
                    dashArray: '4, 4'
                }).addTo(mapInstance.current);

                // Inline badge marker
                const icon = L.divIcon({
                    html: createZoneMarkerHtml(z),
                    className: 'custom-zone-marker',
                    iconSize: [80, 24],
                    iconAnchor: [40, 12]
                });
                const marker = L.marker([z.latitude, z.longitude], { icon })
                    .addTo(mapInstance.current)
                    .bindPopup(`<strong>Zone: ${z.name}</strong><br/>City: ${z.city || '—'}`);

                currentMarkers.zones[z._id] = { circle, marker };
            }
        });

        // ── 2. Render Drivers ──────────────────────────────────────
        // Keep track of active driver IDs in this cycle
        const driverIdsInFilter = new Set();

        filteredDrivers.forEach(d => {
            if (!d.lastLatitude || !d.lastLongitude) return;
            driverIdsInFilter.add(d._id);

            const popupContent = `
                <div style="font-family: sans-serif; min-width: 180px;">
                    <h4 style="margin: 0 0 6px 0; color: #116611;">🚗 ${d.name}</h4>
                    <p style="margin: 0 0 4px 0; font-size: 12px;"><b>Phone:</b> ${d.phoneNumber || '—'}</p>
                    <p style="margin: 0 0 4px 0; font-size: 12px;"><b>Vehicle:</b> ${d.vehicle?.vehicleNumber || '—'} (${d.vehicle?.type?.toUpperCase() || '—'})</p>
                    <p style="margin: 0 0 4px 0; font-size: 12px;"><b>Availability:</b> 
                       <span style="color: ${d.isAvailable ? '#4caf50' : '#ff9800'}; font-weight: bold;">
                          ${d.isAvailable ? 'Available' : 'On Booking'}
                       </span>
                    </p>
                    <p style="margin: 0; font-size: 11px; color: #666;"><b>Last Address:</b> ${d.lastAddress || '—'}</p>
                </div>
            `;

            if (currentMarkers.drivers[d._id]) {
                // Update position & icon
                currentMarkers.drivers[d._id].setLatLng([d.lastLatitude, d.lastLongitude]);
                currentMarkers.drivers[d._id].setIcon(L.divIcon({
                    html: createDriverMarkerHtml(d, d.isAvailable),
                    className: 'custom-driver-marker',
                    iconSize: [48, 48],
                    iconAnchor: [24, 24]
                }));
                currentMarkers.drivers[d._id].getPopup().setContent(popupContent);
            } else {
                // Create new
                const icon = L.divIcon({
                    html: createDriverMarkerHtml(d, d.isAvailable),
                    className: 'custom-driver-marker',
                    iconSize: [48, 48],
                    iconAnchor: [24, 24]
                });
                const marker = L.marker([d.lastLatitude, d.lastLongitude], { icon })
                    .addTo(mapInstance.current)
                    .bindPopup(popupContent);
                
                marker.on('click', () => setSelectedItemId(d._id));
                currentMarkers.drivers[d._id] = marker;
            }
        });

        // Prune drivers that disappeared or filtered out
        Object.keys(currentMarkers.drivers).forEach(id => {
            if (!driverIdsInFilter.has(id)) {
                mapInstance.current.removeLayer(currentMarkers.drivers[id]);
                delete currentMarkers.drivers[id];
            }
        });

        // ── 3. Render Customers / Bookings ─────────────────────────
        const bookingIdsInFilter = new Set();

        filteredBookings.forEach(b => {
            if (!b.pickupLocation?.latitude || !b.pickupLocation?.longitude) return;
            bookingIdsInFilter.add(b._id);

            const statusText = STATUS_CONFIG[b.status]?.label || b.status;
            const statusColor = STATUS_CONFIG[b.status]?.color || '#757575';
            
            const popupContent = `
                <div style="font-family: sans-serif; min-width: 200px;">
                    <h4 style="margin: 0 0 6px 0; color: #d32f2f;">📍 Booking Info</h4>
                    <p style="margin: 0 0 4px 0; font-size: 11px; font-family: monospace;"><b>ID:</b> ${b._id.slice(-8).toUpperCase()}</p>
                    <p style="margin: 0 0 4px 0; font-size: 12px;"><b>Customer:</b> ${b.customer?.name || '—'}</p>
                    <p style="margin: 0 0 4px 0; font-size: 12px;"><b>Phone:</b> ${b.customer?.phoneNumber || '—'}</p>
                    <p style="margin: 0 0 4px 0; font-size: 12px;"><b>Vehicle Needed:</b> ${b.vehicleType?.toUpperCase() || '—'}</p>
                    <p style="margin: 0 0 6px 0; font-size: 12px;"><b>Status:</b> 
                       <span style="background: ${statusColor}; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold;">
                          ${statusText}
                       </span>
                    </p>
                    ${b.arrivalTime ? `<p style="margin: 0 0 4px 0; font-size: 11px; color: #555;"><b>Arrived:</b> ${new Date(b.arrivalTime).toLocaleTimeString('en-IN')}${b.autoArrived ? ' (Auto)' : ''}</p>` : ''}
                    ${b.serviceStartTime ? `<p style="margin: 0 0 4px 0; font-size: 11px; color: #555;"><b>Started:</b> ${new Date(b.serviceStartTime).toLocaleTimeString('en-IN')}${b.autoStarted ? ' (Auto)' : ''}</p>` : ''}
                    <p style="margin: 0; font-size: 11px; color: #555;"><b>Pickup Address:</b> ${b.pickupAddress}</p>
                </div>
            `;

            // Draw pickup pin
            if (currentMarkers.bookings[b._id]) {
                currentMarkers.bookings[b._id].setLatLng([b.pickupLocation.latitude, b.pickupLocation.longitude]);
                currentMarkers.bookings[b._id].getPopup().setContent(popupContent);
            } else {
                const icon = L.divIcon({
                    html: createCustomerMarkerHtml(b),
                    className: 'custom-customer-marker',
                    iconSize: [34, 34],
                    iconAnchor: [17, 17]
                });
                const marker = L.marker([b.pickupLocation.latitude, b.pickupLocation.longitude], { icon })
                    .addTo(mapInstance.current)
                    .bindPopup(popupContent);

                marker.on('click', () => setSelectedItemId(b._id));
                currentMarkers.bookings[b._id] = marker;
            }

            // Draw polyline connecting driver to pickup if assigned and coordinates are valid
            const driverCoord = b.driverLocation;
            if (driverCoord && driverCoord.latitude && driverCoord.longitude) {
                const points = [
                    [driverCoord.latitude, driverCoord.longitude],
                    [b.pickupLocation.latitude, b.pickupLocation.longitude]
                ];

                if (currentMarkers.lines[b._id]) {
                    currentMarkers.lines[b._id].setLatLngs(points);
                } else {
                    const polyline = L.polyline(points, {
                        color: '#ff9800',
                        weight: 3.5,
                        dashArray: '6, 8',
                        opacity: 0.8
                    }).addTo(mapInstance.current);
                    currentMarkers.lines[b._id] = polyline;
                }
            } else {
                // If it was assigned before but driver coords are now lost, delete polyline
                if (currentMarkers.lines[b._id]) {
                    mapInstance.current.removeLayer(currentMarkers.lines[b._id]);
                    delete currentMarkers.lines[b._id];
                }
            }
        });

        // Prune bookings & lines that disappeared/filtered out
        Object.keys(currentMarkers.bookings).forEach(id => {
            if (!bookingIdsInFilter.has(id)) {
                mapInstance.current.removeLayer(currentMarkers.bookings[id]);
                delete currentMarkers.bookings[id];

                if (currentMarkers.lines[id]) {
                    mapInstance.current.removeLayer(currentMarkers.lines[id]);
                    delete currentMarkers.lines[id];
                }
            }
        });

        // Auto-center view if initial markers loaded
        const allCoords = [];
        filteredDrivers.forEach(d => {
            if (d.lastLatitude && d.lastLongitude) allCoords.push([d.lastLatitude, d.lastLongitude]);
        });
        filteredBookings.forEach(b => {
            if (b.pickupLocation?.latitude && b.pickupLocation?.longitude) {
                allCoords.push([b.pickupLocation.latitude, b.pickupLocation.longitude]);
            }
        });

        if (allCoords.length > 0 && !selectedItemId) {
            const bounds = L.latLngBounds(allCoords);
            mapInstance.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
        }

    }, [bookings, drivers, zones, searchQuery, selectedStatus, selectedZone, driverAvailFilter, selectedVehicleType, leafletLoaded, selectedItemId]);

    // Zoom card callback
    const handleZoomToItem = (item, type) => {
        if (!mapInstance.current) return;
        setSelectedItemId(item._id);

        if (type === 'booking') {
            const lat = item.pickupLocation?.latitude;
            const lng = item.pickupLocation?.longitude;
            if (lat && lng) {
                mapInstance.current.setView([lat, lng], 15);
                const marker = markersRef.current.bookings[item._id];
                if (marker) marker.openPopup();
            }
        } else if (type === 'driver') {
            const lat = item.lastLatitude;
            const lng = item.lastLongitude;
            if (lat && lng) {
                mapInstance.current.setView([lat, lng], 15);
                const marker = markersRef.current.drivers[item._id];
                if (marker) marker.openPopup();
            }
        }
    };

    // UI filters
    const vehicleTypes = ['tractor', 'tanker', 'excavator'];

    return (
        <Box sx={{ height: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column' }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight={850} color="text.primary">📡 Live Map Tracking</Typography>
                    <Typography variant="body2" color="text.secondary">Real-time driver location tracking, active dispatch requests, and booking statuses.</Typography>
                </Box>
                <Button
                    variant="outlined" 
                    startIcon={<Refresh />} 
                    onClick={() => fetchLiveMapData(true)}
                    size="small"
                    sx={{ borderRadius: 2 }}
                >
                    Refresh
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Split Screen Layout */}
            <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                {/* Left Side: Sidebar Filters & Lists */}
                <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                        {/* Search Input */}
                        <TextField
                            fullWidth
                            placeholder="Search names, phone, bookings..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="small"
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />

                        {/* Collapsible Accordion or filters list */}
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}>
                            <FilterList fontSize="small" /> Filters
                        </Typography>

                        <Grid container spacing={1} sx={{ mb: 2 }}>
                            {/* Booking Status */}
                            <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Booking Status</InputLabel>
                                    <Select
                                        value={selectedStatus}
                                        label="Booking Status"
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                    >
                                        <MenuItem value="all">All Statuses</MenuItem>
                                        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                                            <MenuItem key={k} value={k}>{v.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Driver Availability */}
                            <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Driver Status</InputLabel>
                                    <Select
                                        value={driverAvailFilter}
                                        label="Driver Status"
                                        onChange={(e) => setDriverAvailFilter(e.target.value)}
                                    >
                                        <MenuItem value="all">All Availability</MenuItem>
                                        <MenuItem value="available">Available (Free)</MenuItem>
                                        <MenuItem value="busy">Busy (On Duty)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Vehicle Type */}
                            <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Vehicle Type</InputLabel>
                                    <Select
                                        value={selectedVehicleType}
                                        label="Vehicle Type"
                                        onChange={(e) => setSelectedVehicleType(e.target.value)}
                                    >
                                        <MenuItem value="all">All Vehicles</MenuItem>
                                        {vehicleTypes.map(v => (
                                            <MenuItem key={v} value={v}>{v.toUpperCase()}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Active Zones */}
                            <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Operating Zone</InputLabel>
                                    <Select
                                        value={selectedZone}
                                        label="Operating Zone"
                                        onChange={(e) => setSelectedZone(e.target.value)}
                                    >
                                        <MenuItem value="all">All Zones</MenuItem>
                                        {zones.map(z => (
                                            <MenuItem key={z._id} value={z._id}>{z.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Divider sx={{ mb: 1.5 }} />

                        {/* Booking & Driver Quick List */}
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {/* Active bookings label */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', mb: 1 }}>
                                Active Dispatch Bookings ({bookings.filter(b => selectedStatus === 'all' || b.status === selectedStatus).length})
                            </Typography>
                            {bookings.length === 0 ? (
                                <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                                    <HourglassEmpty sx={{ fontSize: 24, mb: 1, opacity: 0.5 }} />
                                    <Typography variant="body2">No active bookings under tracking.</Typography>
                                </Box>
                            ) : (
                                <List dense sx={{ p: 0, mb: 2 }}>
                                    {bookings.map(b => (
                                        <ListItem
                                            button
                                            key={b._id}
                                            onClick={() => handleZoomToItem(b, 'booking')}
                                            sx={{
                                                borderRadius: 1.5,
                                                mb: 0.8,
                                                border: selectedItemId === b._id ? '2px solid #f44336' : '1px solid #eef2f6',
                                                bgcolor: selectedItemId === b._id ? 'rgba(244,67,54,0.04)' : 'transparent',
                                                '&:hover': { bgcolor: '#f7f9fc' }
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: '#ffebee', color: '#f44336' }}>
                                                    <People fontSize="small" />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {b.customer?.name}
                                                        </Typography>
                                                        <Chip
                                                            label={STATUS_CONFIG[b.status]?.label || b.status}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: STATUS_CONFIG[b.status]?.color || '#ccc',
                                                                color: 'white',
                                                                height: 18,
                                                                fontSize: 10,
                                                                fontWeight: 700
                                                            }}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                        Booking #{b._id.slice(-6).toUpperCase()} · {b.vehicleType?.toUpperCase()} · {b.pickupAddress}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            {/* Active drivers list label */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', mb: 1 }}>
                                Online Drivers ({drivers.length})
                            </Typography>
                            {drivers.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>No drivers active.</Typography>
                            ) : (
                                <List dense sx={{ p: 0 }}>
                                    {drivers.map(d => (
                                        <ListItem
                                            button
                                            key={d._id}
                                            onClick={() => handleZoomToItem(d, 'driver')}
                                            sx={{
                                                borderRadius: 1.5,
                                                mb: 0.8,
                                                border: selectedItemId === d._id ? '2px solid #4caf50' : '1px solid #eef2f6',
                                                bgcolor: selectedItemId === d._id ? 'rgba(76,175,80,0.04)' : 'transparent',
                                                '&:hover': { bgcolor: '#f7f9fc' }
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: d.isAvailable ? '#e8f5e9' : '#fff3e0', color: d.isAvailable ? '#4caf50' : '#ff9800' }}>
                                                    <DirectionsCar fontSize="small" />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {d.name}
                                                        </Typography>
                                                        <Chip
                                                            label={d.isAvailable ? 'Available' : 'Busy'}
                                                            size="small"
                                                            color={d.isAvailable ? 'success' : 'warning'}
                                                            variant="outlined"
                                                            sx={{ height: 18, fontSize: 9, fontWeight: 700 }}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                        {d.vehicle?.vehicleNumber || 'No vehicle'} ({d.vehicle?.type?.toUpperCase() || ''}) · {d.lastAddress || 'No GPS fix'}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Side: Map Container */}
                <Grid item xs={12} md={8} sx={{ height: '100%', position: 'relative' }}>
                    <Paper
                        sx={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            borderRadius: 3,
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #eef2f6'
                        }}
                    >
                        {loading && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                zIndex: 1000,
                                bgcolor: 'rgba(255,255,255,0.7)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexDirection: 'column',
                                gap: 1
                            }}>
                                <CircularProgress />
                                <Typography variant="body2" fontWeight={600}>Loading map modules…</Typography>
                            </Box>
                        )}
                        <div
                            ref={mapRef}
                            style={{ width: '100%', height: '100%', zIndex: 1 }}
                        />
                        {/* Map floating labels legend */}
                        <Box sx={{
                            position: 'absolute',
                            bottom: 15,
                            left: 15,
                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                            padding: '8px 12px',
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            zIndex: 10,
                            display: 'flex',
                            gap: 1.5,
                            fontSize: 11,
                            fontFamily: 'sans-serif'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ width: 10, height: 10, backgroundColor: '#f44336', borderRadius: '50%', display: 'inline-block' }}></span>
                                <span>Customer Pin</span>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ width: 10, height: 10, backgroundColor: '#4caf50', borderRadius: '50%', display: 'inline-block' }}></span>
                                <span>Available Driver</span>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ width: 10, height: 10, backgroundColor: '#ff9800', borderRadius: '50%', display: 'inline-block' }}></span>
                                <span>Busy Driver</span>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ borderBottom: '2px dashed #ff9800', width: 20, display: 'inline-block' }}></span>
                                <span>Dispatch Route</span>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default LiveMap;
